<template>
  <LgModal :open="open" title="Add a tile" @close="$emit('close')">
    <div class="add-tile">
      <!-- 1. What kind of tile? -->
      <fieldset class="field">
        <legend class="field-label">What should this tile show?</legend>
        <div class="type-row" role="radiogroup" aria-label="Tile type">
          <button
            type="button"
            class="type-btn"
            :class="{ active: tileType === 'metric' }"
            role="radio"
            :aria-checked="tileType === 'metric'"
            @click="tileType = 'metric'"
          >
            <ChartSpline :size="18" aria-hidden="true" />
            <span class="type-name">Metric chart</span>
            <span class="type-sub">A chart or single value from a metric</span>
          </button>
          <button
            type="button"
            class="type-btn"
            :class="{ active: tileType === 'logs' }"
            role="radio"
            :aria-checked="tileType === 'logs'"
            @click="tileType = 'logs'"
          >
            <ScrollText :size="18" aria-hidden="true" />
            <span class="type-name">Logs list</span>
            <span class="type-sub">The latest matching log lines</span>
          </button>
        </div>
      </fieldset>

      <!-- 2. Where does it come from? -->
      <fieldset class="field">
        <legend class="field-label">
          {{ tileType === 'metric' ? 'Which metric?' : 'Which logs?' }}
        </legend>
        <div class="mode-row" role="radiogroup" aria-label="Tile source">
          <button
            type="button"
            class="mode-btn"
            :class="{ active: sourceMode === 'saved' }"
            role="radio"
            :aria-checked="sourceMode === 'saved'"
            @click="sourceMode = 'saved'"
          >
            {{ tileType === 'metric' ? 'A saved exploration' : 'A saved filter set' }}
          </button>
          <button
            type="button"
            class="mode-btn"
            :class="{ active: sourceMode === 'inline' }"
            role="radio"
            :aria-checked="sourceMode === 'inline'"
            @click="sourceMode = 'inline'"
          >
            Build it here
          </button>
        </div>

        <!-- Metric · saved exploration -->
        <template v-if="tileType === 'metric' && sourceMode === 'saved'">
          <LgSelect
            v-if="explorationOptions.length"
            :model-value="explorationId"
            :options="explorationOptions"
            placeholder="Pick a saved exploration…"
            aria-label="Saved exploration"
            @update:model-value="onPickExploration"
          />
          <p v-else class="hint">
            {{
              explorations.loading.value
                ? 'Loading saved explorations…'
                : 'No saved explorations yet — build a chart on the Metrics page and save it, or build one here.'
            }}
          </p>
        </template>

        <!-- Metric · inline -->
        <template v-if="tileType === 'metric' && sourceMode === 'inline'">
          <LgSelect
            v-if="catalogOptions.length"
            :model-value="metricKey"
            :options="catalogOptions"
            placeholder="Pick a metric…"
            aria-label="Metric"
            @update:model-value="onPickMetric"
          />
          <p v-else class="hint">
            {{ catalogLoading ? 'Loading metrics…' : 'No metrics found in the last hour.' }}
          </p>
          <label v-if="metricKey" class="opt">
            <span class="opt-label">Aggregation</span>
            <LgSelect
              :model-value="agg"
              :options="aggSelectOptions"
              aria-label="Aggregation"
              @update:model-value="agg = $event as MetricAgg"
            />
          </label>
        </template>

        <!-- Metric · viz (both modes) -->
        <label v-if="tileType === 'metric'" class="opt">
          <span class="opt-label">Show as</span>
          <VizTypePicker
            :model-value="vizPickerValue"
            :options="['line', 'bar', 'area', 'counter']"
            aria-label="Tile chart type"
            @update:model-value="onVizChange"
          />
          <span class="opt-hint">Line, bar, or area chart — or a single big number.</span>
        </label>

        <!-- Logs · saved filter set -->
        <template v-if="tileType === 'logs' && sourceMode === 'saved'">
          <LgSelect
            v-if="filterSetOptions.length"
            :model-value="filterSetId"
            :options="filterSetOptions"
            placeholder="Pick a saved filter set…"
            aria-label="Saved filter set"
            @update:model-value="onPickFilterSet"
          />
          <p v-else class="hint">
            {{
              filterSets.loading.value
                ? 'Loading filter sets…'
                : 'No saved filter sets yet — save one from the Logs page, or build a query here.'
            }}
          </p>
        </template>

        <!-- Logs · inline query -->
        <template v-if="tileType === 'logs' && sourceMode === 'inline'">
          <LgInput
            :model-value="inlineQuery"
            placeholder="e.g. levels=error"
            aria-label="Logs filter query"
            @update:model-value="inlineQuery = $event"
          />
          <p class="hint">
            Leave empty to show all logs, or paste the query part of a filtered Logs page URL.
          </p>
        </template>
      </fieldset>

      <!-- 3. Optional title -->
      <label class="field">
        <span class="field-label">Title <span class="optional">(optional)</span></span>
        <LgInput
          :model-value="title"
          :placeholder="titlePlaceholder"
          aria-label="Tile title"
          @update:model-value="title = $event"
        />
      </label>
    </div>

    <template #footer>
      <LgButton variant="ghost" @click="$emit('close')">Cancel</LgButton>
      <LgButton variant="primary" :disabled="!canAdd" @click="onAdd">Add tile</LgButton>
    </template>
  </LgModal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { ChartSpline, ScrollText } from 'lucide-vue-next';
import {
  api,
  type CatalogMetric,
  type MetricAgg,
  type MetricTileViz,
  type ResolvedLogsTileConfig,
  type ResolvedMetricTileConfig,
  type ResolvedTile,
  type TileType,
} from '../../api.js';
import { RANGE_MS } from '../../composables/useFilters.js';
import { useExplorations } from '../../composables/useExplorations.js';
import { useFilterSets } from '../../composables/useFilterSets.js';
import { aggOptions, defaultAgg, type ExplorationSpec } from '../../lib/metric-exploration.js';
import {
  defaultSize,
  newTileId,
  humanizeMetricName,
  DEFAULT_LOGS_TILE_LIMIT,
} from '../../lib/dashboard-layout.js';
import type { VizType } from '../../ui/VizTypePicker.vue';
import VizTypePicker from '../../ui/VizTypePicker.vue';
import LgModal from '../../ui/LgModal.vue';
import LgSelect from '../../ui/LgSelect.vue';
import LgInput from '../../ui/LgInput.vue';
import LgButton from '../../ui/LgButton.vue';

const props = defineProps<{ open: boolean }>();
const emit = defineEmits<{ close: []; add: [tile: ResolvedTile] }>();

const tileType = ref<TileType>('metric');
const sourceMode = ref<'saved' | 'inline'>('saved');
const title = ref('');

// Metric state
const explorations = useExplorations();
const explorationId = ref('');
const catalog = ref<CatalogMetric[]>([]);
const catalogLoading = ref(false);
const metricKey = ref('');
const agg = ref<MetricAgg>('avg');
const viz = ref<MetricTileViz>('line');

// Logs state
const filterSets = useFilterSets();
const filterSetId = ref('');
const inlineQuery = ref('');

let loadedOnce = false;
watch(
  () => props.open,
  (open) => {
    if (!open) return;
    // Reset the per-open choices but keep the loaded lists warm.
    title.value = '';
    explorationId.value = '';
    metricKey.value = '';
    filterSetId.value = '';
    inlineQuery.value = '';
    viz.value = 'line';
    if (loadedOnce) return;
    loadedOnce = true;
    explorations.load();
    filterSets.load();
    loadCatalog();
  },
);

async function loadCatalog() {
  catalogLoading.value = true;
  try {
    const now = Date.now();
    const res = await api.metrics.catalog({ from: now - RANGE_MS['1h'], to: now });
    catalog.value = res.metrics;
  } catch {
    catalog.value = [];
  } finally {
    catalogLoading.value = false;
  }
}

const explorationOptions = computed(() =>
  explorations.items.value.map((e) => ({ value: String(e.id), label: e.name })),
);
const filterSetOptions = computed(() =>
  filterSets.sets.value.map((s) => ({ value: String(s.id), label: s.name })),
);
const catalogOptions = computed(() =>
  catalog.value.map((m) => ({
    value: `${m.source} ${m.target} ${m.name}`,
    label: `${humanizeMetricName(m.name)} — ${m.target}`,
  })),
);
const pickedMetric = computed<CatalogMetric | null>(() => {
  if (!metricKey.value) return null;
  const [source, target, name] = metricKey.value.split(' ');
  return (
    catalog.value.find((m) => m.source === source && m.target === target && m.name === name) ?? null
  );
});
const aggSelectOptions = computed(() =>
  aggOptions(pickedMetric.value?.type).map((a) => ({ value: a, label: a })),
);

const pickedExploration = computed(() =>
  explorations.items.value.find((e) => String(e.id) === explorationId.value),
);

// The dashboard `stat` viz maps to the picker's `counter` icon (single value).
const vizPickerValue = computed<VizType>(() => (viz.value === 'stat' ? 'counter' : viz.value));
function onVizChange(v: VizType) {
  viz.value = v === 'counter' ? 'stat' : (v as MetricTileViz);
}

function onPickExploration(id: string) {
  explorationId.value = id;
  const spec = pickedExploration.value?.spec as ExplorationSpec | undefined;
  if (spec) {
    // Seed the tile viz from the exploration's own viz (single-value → stat).
    if (spec.viz === 'counter' || spec.viz === 'gauge') viz.value = 'stat';
    else if (spec.viz === 'bar' || spec.viz === 'area') viz.value = spec.viz;
    else viz.value = 'line';
  }
}

function onPickMetric(key: string) {
  metricKey.value = key;
  agg.value = defaultAgg(pickedMetric.value?.type);
}

function onPickFilterSet(id: string) {
  filterSetId.value = id;
}

const titlePlaceholder = computed(() => {
  if (tileType.value === 'metric') {
    if (sourceMode.value === 'saved') return pickedExploration.value?.name ?? 'e.g. API latency';
    const m = pickedMetric.value;
    return m ? humanizeMetricName(m.name) : 'e.g. API latency';
  }
  if (sourceMode.value === 'saved') {
    return filterSets.sets.value.find((s) => String(s.id) === filterSetId.value)?.name ?? 'Logs';
  }
  return 'Logs';
});

const canAdd = computed(() => {
  if (tileType.value === 'metric') {
    return sourceMode.value === 'saved' ? explorationId.value !== '' : pickedMetric.value !== null;
  }
  return sourceMode.value === 'saved' ? filterSetId.value !== '' : true; // empty inline = all logs
});

/** Build the tile in RESOLVED form (we already hold the spec/query locally) so
 * the view can render it immediately without a `/resolved` round-trip; the
 * save path strips it back to the raw saved-ref shape (`rawTile`). */
function onAdd() {
  const dims = defaultSize(tileType.value);
  const base = {
    id: newTileId(),
    x: 0,
    y: 0, // the view normalizes y on append
    w: dims.w,
    h: dims.h,
    ...(title.value.trim() ? { title: title.value.trim() } : {}),
  };
  let tile: ResolvedTile;
  if (tileType.value === 'metric') {
    const config: ResolvedMetricTileConfig =
      sourceMode.value === 'saved'
        ? {
            source: {
              kind: 'inline',
              explorationId: Number(explorationId.value),
              spec: pickedExploration.value?.spec,
            },
            viz: viz.value,
          }
        : {
            source: { kind: 'inline', metric: pickedMetric.value!.name, agg: agg.value },
            viz: viz.value,
          };
    tile = { ...base, type: 'metric', config };
  } else {
    const config: ResolvedLogsTileConfig =
      sourceMode.value === 'saved'
        ? {
            source: {
              kind: 'inline',
              filterSetId: Number(filterSetId.value),
              query: filterSets.sets.value.find((s) => String(s.id) === filterSetId.value)?.query,
            },
            limit: DEFAULT_LOGS_TILE_LIMIT,
          }
        : {
            source: { kind: 'inline', query: inlineQuery.value.trim() },
            limit: DEFAULT_LOGS_TILE_LIMIT,
          };
    tile = { ...base, type: 'logs', config };
  }
  emit('add', tile);
}
</script>

<style scoped>
.add-tile {
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-4);
}
.field {
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-2);
  border: none;
  margin: 0;
  padding: 0;
  min-width: 0;
}
.field-label {
  font-size: var(--lg-fs-xs);
  color: var(--lg-text-faint);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0;
}
.optional {
  text-transform: none;
  letter-spacing: normal;
}
.type-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--lg-space-2);
}
.type-btn {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--lg-space-1);
  padding: var(--lg-space-3);
  font: inherit;
  text-align: left;
  background: var(--lg-bg-surface);
  color: var(--lg-text-dim);
  border: 1px solid var(--lg-border-strong);
  border-radius: var(--lg-radius-md);
  cursor: pointer;
  min-width: 0;
}
.type-btn:hover {
  color: var(--lg-text);
}
.type-btn.active {
  border-color: var(--lg-accent);
  color: var(--lg-text);
  box-shadow: inset 0 0 0 1px var(--lg-accent);
}
.type-name {
  font-size: var(--lg-fs-sm);
  font-weight: 600;
}
.type-sub {
  font-size: var(--lg-fs-xs);
  color: var(--lg-text-faint);
}
.type-btn:focus-visible {
  outline: none;
  box-shadow: var(--lg-focus);
}
.mode-row {
  display: inline-flex;
  border: 1px solid var(--lg-border-strong);
  border-radius: var(--lg-radius-md);
  overflow: hidden;
  align-self: flex-start;
}
.mode-btn {
  font: inherit;
  font-size: var(--lg-fs-sm);
  min-height: var(--lg-control-md);
  padding: 0 var(--lg-space-3);
  background: var(--lg-bg-surface);
  color: var(--lg-text-dim);
  border: none;
  border-right: 1px solid var(--lg-border);
  cursor: pointer;
}
.mode-btn:last-child {
  border-right: none;
}
.mode-btn:hover {
  color: var(--lg-text);
}
.mode-btn.active {
  background: var(--lg-accent);
  color: var(--lg-accent-fg);
  font-weight: 600;
}
.mode-btn:focus-visible {
  outline: none;
  box-shadow: inset 0 0 0 2px var(--lg-accent);
}
.opt {
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-1);
}
/* The viz picker should hug its four options, not stretch across the modal. */
.opt .lg-viz-picker {
  align-self: flex-start;
}
.opt-label {
  font-size: var(--lg-fs-xs);
  color: var(--lg-text-faint);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.opt-hint,
.hint {
  margin: 0;
  font-size: var(--lg-fs-xs);
  color: var(--lg-text-faint);
}
</style>
